import React, { useCallback, useState } from 'react';
import ReactGA from 'react-ga4';

import { SITE_LINKS } from '../constants';
import {
  ActivityCompletedData,
  CustomGAEventData,
  LessonRatedEventData,
} from '../declarations/player-events';
import { LessonEvent } from '../enum';
import { GoogleAnalyticsEvents } from '../enum/GoogleAnalyticsEvents';
import { Activity } from '../models/Activity';
import {
  postActivityCompleted,
  postContinuePressed,
  postLessonCompleted,
  postLessonFeedback,
  postPlayerClosed,
} from '../services/interactions';

export const usePlayerPostMessage = () => {
  const [_activity, _setActivity] = useState<Activity | null>();

  const activityRef = React.useRef(_activity);
  const setActivity = (activity: Activity) => {
    activityRef.current = activity;
    _setActivity(activity);
  };

  const listen = useCallback(() => {
    window.addEventListener('message', handler);
  }, []);

  const stopListening = useCallback(() => {
    window.removeEventListener('message', handler);
  }, []);

  function handler(event: MessageEvent) {
    const type = event.data?.type;

    if (!isPlayerMessage(type)) {
      return;
    }

    if (type === LessonEvent.LESSON_RATED) {
      return handleLessonRated(event.data);
    } else if (type === LessonEvent.ACTIVITY_COMPLETED) {
      return handleActivityCompleted(event.data);
    } else if (type === LessonEvent.LESSON_COMPLETED) {
      return handleLessonCompleted();
    } else if (type === LessonEvent.CONTINUE_PRESSED) {
      return handleContinuePressed();
    } else if (type === LessonEvent.PLAYER_CLOSED) {
      return handlePlayerClosed();
    } else if (type === LessonEvent.CUSTOM_GA_EVENT) {
      return handleCustomEvent(event.data);
    } else {
      console.log('Player Post Message Handler not implemented yet.', event.data);
    }
  }

  const handleCustomEvent = (eventData: CustomGAEventData) => {
    const { name, data } = eventData;
    const activity = activityRef.current;
    ReactGA.event(name, { ...activity, ...data });
  };

  const handleActivityCompleted = async (eventData: ActivityCompletedData) => {
    const activity = activityRef.current;
    setActivity({
      ...activity,
      startAt: eventData.startAt,
      endAt: eventData.endAt,
    });
    ReactGA.event(GoogleAnalyticsEvents.ActivityCompleted, { ...activity, ...eventData });
    await postActivityCompleted({
      player_lesson_id: activity?.playerLessonId,
      lesson_position: activity?.lessonPosition,
      unit_position: activity?.unitPosition,
      level_position: activity?.levelPosition,
      start_at: eventData.startAt,
      end_at: eventData.endAt,
      activity_number: eventData.activityNumber,
      activity_id: eventData.elementId,
    });
  };

  const handleLessonRated = async (eventData: LessonRatedEventData) => {
    const activity = activityRef.current;
    try {
      ReactGA.event(GoogleAnalyticsEvents.LessonRated, { ...activity, ...eventData });
      await postLessonFeedback({
        player_lesson_id: activity?.playerLessonId,
        lesson_position: activity?.lessonPosition,
        unit_position: activity?.unitPosition,
        level_position: activity?.levelPosition,
        survey_result: eventData.isFeedbackPositive,
      });
    } catch (error) {
      setActivity({
        ...activity,
        isError: !!error,
      });
    }
  };

  const handleLessonCompleted = async () => {
    const activity = activityRef.current;
    ReactGA.event(GoogleAnalyticsEvents.LessonCompleted, activity);
    await postLessonCompleted({
      player_lesson_id: activity?.playerLessonId,
      lesson_position: activity?.lessonPosition,
      unit_position: activity?.unitPosition,
      level_position: activity?.levelPosition,
    });
  };

  const handleContinuePressed = async () => {
    const activity = activityRef.current;
    await postContinuePressed({
      player_lesson_id: activity?.playerLessonId,
      lesson_position: activity?.lessonPosition,
      unit_position: activity?.unitPosition,
      level_position: activity?.levelPosition,
    });
    window.location.href = SITE_LINKS.UnitPage(activity?.levelPosition, activity?.unitPosition);
  };

  const handlePlayerClosed = async () => {
    const activity = activityRef.current;
    await postPlayerClosed({
      player_lesson_id: activity?.playerLessonId,
      lesson_position: activity?.lessonPosition,
      unit_position: activity?.unitPosition,
      level_position: activity?.levelPosition,
    });
    window.location.href = SITE_LINKS.UnitPage(activity?.levelPosition, activity?.unitPosition);
  };

  function isPlayerMessage(type: string): boolean {
    return [
      LessonEvent.ACTIVITY_COMPLETED as string,
      LessonEvent.LESSON_COMPLETED as string,
      LessonEvent.CONTINUE_PRESSED as string,
      LessonEvent.LESSON_RATED as string,
      LessonEvent.PLAYER_CLOSED as string,
      LessonEvent.CUSTOM_GA_EVENT as string,
    ].includes(type);
  }

  return {
    listen,
    stopListening,
    setActivity,
    activity: _activity,
  };
};
